import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { useWriteValidationError } from "@/shared/lib/errors/err-validation-failed-context";
import { WorkOrder } from "@/services/backend/htz/work-order/types/work-order";
import {
  useHtzWorkOrderPdfDownloadMutation,
  useLazyHtzWorkOrderPdfPreviewQuery,
  WorkOrderDocument,
} from "@/services/backend/htz/work-order/api/work-order-api";

export function WorkOrderPdf({ workOrder }: { workOrder: WorkOrder }) {
  const previewRequest = {
    workOrderId: workOrder.id,
    document: WorkOrderDocument.ALL,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyHtzWorkOrderPdfPreviewQuery();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Arbeitsschein")}
              documentNumber={
                <ReferenceNumber referenceNumber={workOrder.referenceNumber} />
              }
            />
            <DocumentSheetActionsContent>
              <DocumentNotReadyAlert document={t("Arbeitsschein")} />
              <DocumentSheetImmutabilityWarning
                title={t("Hinweis")}
                description={t(
                  "Arbeitsscheine werden nicht festgeschrieben. Sie können sie jederzeit herunterladen.",
                )}
              />
              <Separator />
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  workOrderId={workOrder.id}
                  document={WorkOrderDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  workOrderId={workOrder.id}
                  document={WorkOrderDocument.WORK_ORDER}
                  text={t("Arbeitsschein")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  workOrderId,
  document: documentType,
  text,
}: {
  workOrderId: string;
  document: WorkOrderDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useHtzWorkOrderPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      workOrderId,
      document: documentType,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Arbeitsschein herunterladen")}</DialogTitle>
          <DialogDescription>
            {t("Möchten Sie den Arbeitsschein herunterladen?")}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
